import type * as React from "react";

import { concat, isArray, range, sortBy, uniq } from "../../lib/utils";
import MinuteView from "../../views/MinuteView";
import {
  type BasePickerOnChangeData,
  type BasePickerProps,
  type DisableValuesProps,
  type MinMaxValueProps,
  type OptionalHeaderProps,
  type ProvideHeadingValue,
  SingleSelectionPicker,
  type TimeFormat,
  type TimePickerProps,
} from "../BasePicker";
import {
  buildTimeStringWithSuffix,
  getCurrentDate,
  isNextPageAvailable,
  isPrevPageAvailable,
} from "./sharedFunctions";

const MINUTES_STEP = 5;
const PAGE_WIDTH_DEFAULT = 3;
const PAGE_WIDTH_WIDE = 4;

function getMinutePageWidth(minuteStep: number): 3 | 4 {
  const count = Math.floor(60 / minuteStep);
  return count % 4 === 0 ? PAGE_WIDTH_WIDE : PAGE_WIDTH_DEFAULT;
}

type MinutePickerProps = BasePickerProps &
  MinMaxValueProps &
  DisableValuesProps &
  TimePickerProps &
  OptionalHeaderProps;

export type { MinutePickerProps };

export interface MinutePickerOnChangeData extends BasePickerOnChangeData {
  value: {
    year: number;
    month: number;
    date: number;
    hour: number;
    minute: number;
  };
}

class MinutePicker
  extends SingleSelectionPicker<MinutePickerProps>
  implements ProvideHeadingValue
{
  public static readonly defaultProps: {
    timeFormat: TimeFormat;
    minuteStep: number;
  } = { timeFormat: "24", minuteStep: MINUTES_STEP };

  constructor(props: MinutePickerProps) {
    super(props);
    this.PAGE_WIDTH = getMinutePageWidth(props.minuteStep ?? MINUTES_STEP);
  }

  public componentDidUpdate(prevProps: MinutePickerProps) {
    if (prevProps.minuteStep !== this.props.minuteStep) {
      this.PAGE_WIDTH = getMinutePageWidth(
        this.props.minuteStep ?? MINUTES_STEP,
      );
    }
  }

  public render() {
    const {
      inline,
      onCalendarViewMount,
      localization,
      onHeaderClick,
      hasHeader,
      pickerWidth,
      pickerStyle,
      minuteStep,
    } = this.props;
    const width = getMinutePageWidth(minuteStep ?? MINUTES_STEP);

    return (
      <MinuteView
        values={this.buildCalendarValues()}
        onNextPageBtnClick={this.switchToNextPage}
        onPrevPageBtnClick={this.switchToPrevPage}
        onValueClick={this.handleChange}
        hoveredItemIndex={this.state.hoveredCellPosition}
        onCellHover={this.onHoveredCellPositionChange}
        onBlur={this.handleBlur}
        inline={inline}
        onMount={onCalendarViewMount}
        hasNextPage={this.isNextPageAvailable()}
        hasPrevPage={this.isPrevPageAvailable()}
        disabledItemIndexes={this.getDisabledPositions()}
        currentHeadingValue={this.getCurrentDate()}
        activeItemIndex={this.getActiveCellPosition()}
        onHeaderClick={onHeaderClick}
        hasHeader={hasHeader ?? false}
        localization={localization}
        pickerWidth={pickerWidth}
        pickerStyle={pickerStyle}
        width={width}
      />
    );
  }

  public getCurrentDate(): string {
    /* Return currently selected month, date and year(string) to display in calendar header. */
    return getCurrentDate(this.state.date);
  }

  protected buildCalendarValues(): string[] {
    /*
      Return array of minutes (strings) like ['16:15', '16:20', ...]
      that used to populate calendar's page.
    */
    const hour =
      this.state.date.hour() < 10
        ? "0" + this.state.date.hour().toString()
        : this.state.date.hour().toString();
    const minuteStep = this.props.minuteStep!;

    return range(0, 60, minuteStep)
      .map((minute) => `${minute < 10 ? "0" : ""}${minute}`)
      .map((minute) =>
        buildTimeStringWithSuffix(hour, minute, this.props.timeFormat!),
      );
  }

  protected getSelectableCellPositions(): number[] {
    const disabled = this.getDisabledPositions();
    const all = range(0, 60 / this.props.minuteStep!);
    if (disabled) {
      return all.filter((pos) => {
        return disabled.indexOf(pos) < 0;
      });
    }

    return all;
  }

  protected getInitialDatePosition(): number | undefined {
    const selectable = this.getSelectableCellPositions();
    const position = getMinuteCellPosition(
      this.state.date.minute(),
      this.props.minuteStep!,
    );
    if (selectable.length === 0) {
      return undefined;
    }
    if (selectable.indexOf(position) < 0) {
      return selectable[0];
    }

    return position;
  }

  protected getDisabledPositions(): number[] | undefined {
    const { disable, minDate, maxDate } = this.props;
    const minuteStep = this.props.minuteStep!;
    let disabledByDisable: number[] = [];
    let disabledByMaxDate: number[] = [];
    let disabledByMinDate: number[] = [];

    if (isArray(disable)) {
      disabledByDisable = concat(
        disabledByDisable,
        disable
          .filter((date) => date.isSame(this.state.date, "day"))
          .map((date) => getMinuteCellPosition(date.minute(), minuteStep)),
      );
    }
    if (minDate) {
      if (minDate.isSame(this.state.date, "hour")) {
        disabledByMinDate = concat(
          disabledByMinDate,
          range(0, minDate.minute()).map((m) =>
            getMinuteCellPosition(m, minuteStep),
          ),
        );
      }
    }
    if (maxDate) {
      if (maxDate.isSame(this.state.date, "hour")) {
        disabledByMaxDate = concat(
          disabledByMaxDate,
          range(maxDate.minute() + minuteStep, 60).map((m) =>
            getMinuteCellPosition(m, minuteStep),
          ),
        );
      }
    }
    const result = sortBy(
      uniq(concat(disabledByDisable, disabledByMaxDate, disabledByMinDate)),
    );
    if (result.length > 0) {
      return result;
    }
  }

  protected getActiveCellPosition(): number | undefined {
    /*
      Return position of a minute that should be displayed as active
      (position in array returned by `this.buildCalendarValues`).
    */
    const { value } = this.props;
    if (value && value.isSame(this.state.date, "date")) {
      return Math.floor(this.props.value!.minutes() / this.props.minuteStep!);
    }
  }

  protected isNextPageAvailable(): boolean {
    return isNextPageAvailable(this.state.date, this.props.maxDate);
  }

  protected isPrevPageAvailable(): boolean {
    return isPrevPageAvailable(this.state.date, this.props.minDate);
  }

  protected handleChange = (
    e: React.SyntheticEvent<HTMLElement> | null,
    { value }: { value: string },
  ): void => {
    const data: MinutePickerOnChangeData = {
      ...this.props,
      value: {
        year: this.state.date.year(),
        month: this.state.date.month(),
        date: this.state.date.date(),
        hour: this.state.date.hour(),
        minute:
          this.buildCalendarValues().indexOf(value) * this.props.minuteStep!,
      },
    };
    this.props.onChange(e, data);
  };

  protected switchToNextPage = (
    e: React.SyntheticEvent<HTMLElement> | null | undefined,
    data: any,
    callback?: () => void,
  ): void => {
    this.setState(({ date }) => {
      const nextDate = date.clone();
      nextDate.add(1, "day");

      return { date: nextDate };
    }, callback);
  };

  protected switchToPrevPage = (
    e: React.SyntheticEvent<HTMLElement> | null | undefined,
    data: any,
    callback?: () => void,
  ): void => {
    this.setState(({ date }) => {
      const prevDate = date.clone();
      prevDate.subtract(1, "day");

      return { date: prevDate };
    }, callback);
  };
}

function getMinuteCellPosition(minute: number, minuteStep: number): number {
  return Math.floor(minute / minuteStep);
}

export default MinutePicker;
